// src/components/Expense/ExpenseTable.jsx
// Component to display expense data in a table format with modern design

import React from 'react';
import RoleBasedComponent from '../common/RoleBasedComponent';
import { Edit2, Trash2 } from 'lucide-react';

/**
 * ExpenseTable Component
 * Displays a list of expenses in a table format with edit and delete actions
 * 
 * @param {Array} expenses - Array of expense objects to display
 * @param {Function} onEdit - Callback function when edit button is clicked
 * @param {Function} onDelete - Callback function when delete button is clicked
 * @param {boolean} isLoading - Whether data is currently loading
 */
const ExpenseTable = ({ expenses, onEdit, onDelete, isLoading }) => {

  /**
   * Handle edit button click
   * @param {Object} expense - The expense item to edit
   */
  const handleEdit = (expense) => {
    if (onEdit) {
      onEdit(expense);
    }
  };

  /**
   * Handle delete button click
   * @param {Object} expense - The expense item to delete
   */
  const handleDelete = (expense) => {
    if (onDelete) {
      onDelete(expense);
    }
  };

  /**
   * Format currency
   */
  const formatCurrency = (amount) => {
    return 'Rs. ' + parseFloat(amount).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  /**
   * Get badge class for expense type
   */
  const getTypeBadgeClass = (type) => {
    const baseClass = 'expense-type-badge';
    const typeClass = `expense-type-${type?.toLowerCase()}`;
    return `${baseClass} ${typeClass}`;
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="inventory-data-table-container">
        <table className="inventory-data-table">
          <thead>
            <tr>
              <th style={{ width: '100px' }}>ID</th>
              <th style={{ width: '150px' }}>Type</th>
              <th>Reason</th>
                   <th>Inventory</th>
                      <th>Created</th>
                             <th>Last Modified</th>
              <th style={{ textAlign: 'right', width: '150px' }}>Amount</th>
              <th style={{ width: '180px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td colSpan="5" className="inventory-loading">
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center',
                  gap: '12px' 
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    border: '4px solid #E5E7EB',
                    borderTop: '4px solid #004080',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                  <span>Loading expense data...</span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Show empty state
  if (!expenses || expenses.length === 0) {
    return (
      <div className="inventory-data-table-container">
        <table className="inventory-data-table">
          <thead>
            <tr>
              <th style={{ width: '100px' }}>ID</th>
              <th style={{ width: '150px' }}>Type</th>
              <th>Reason</th>
                <th>Inventory</th>
                      <th>Created</th>
                             <th>Last Modified</th>
              <th style={{ textAlign: 'right', width: '150px' }}>Amount</th>
              <th style={{ width: '180px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td colSpan="5" className="inventory-empty-state">
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center',
                  gap: '8px' 
                }}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2">
                    <path d="M9 2v6h6V2M19 2v6M5 2v6M3 8h18M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8" />
                    <line x1="9" y1="12" x2="15" y2="12" />
                  </svg>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#374151' }}>
                    No expenses found
                  </div>
                  <div style={{ fontSize: '14px', color: '#6B7280' }}>
                    Try adjusting your filters or add a new expense
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  // Calculate total
  const totalAmount = expenses.reduce((sum, exp) => sum + parseFloat(exp.amount || 0), 0);

  // Render table with data
  return (
    <div className="inventory-data-table-container">
      <table className="inventory-data-table">
        <thead>
          <tr>
            <th style={{ width: '100px' }}>ID</th>
            <th style={{ width: '150px' }}>Type</th>
            <th>Reason</th>
               <th>Inventory</th>
                      <th>Created</th>
                             <th>Last Modified</th>
            <th style={{ textAlign: 'right', width: '150px' }}>Amount</th>
            <th style={{ width: '180px' }}>Actions</th>
          </tr>
        </thead>

        <tbody>
          {expenses.map((expense) => (
            <tr key={expense.id || expense.expenseId}>
              
              {/* Expense ID */}
              <td style={{ fontWeight: '500', color: '#6B7280' }}>
                EXP-{String(expense.expenseId || expense.id).padStart(4, '0')}
              </td>
              
              {/* Expense Type */}
              <td>
                <span className={getTypeBadgeClass(expense.type)}>
                  {expense.type}
                </span>
              </td>
              
              {/* Reason */}
              <td>{expense.reason}</td>
                  <td>{expense.inventory.name}</td>
                       <td>{expense.createdAt}</td>
                          <td>{expense.updatedAt}</td>    
              {/* Amount */}
              <td style={{ 
                textAlign: 'right', 
                fontWeight: '600', 
                fontSize: '15px',
                color: '#DC2626'
              }}>
                {formatCurrency(expense.amount)}
              </td>
              
              {/* Actions */}
              <td>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    className="inventory-btn-action inventory-btn-edit"
                    onClick={() => handleEdit(expense)}
                    title="Edit this expense"
                  >
                    <Edit2 size={14} />
                    Edit
                  </button>
                  <RoleBasedComponent allowedRoles={['superadmin']}>
                    <button
                      className="inventory-btn-action inventory-btn-delete"
                      onClick={() => handleDelete(expense)}
                      title="Delete this expense"
                    >
                      <Trash2 size={14} />
                      Delete
                    </button>
                  </RoleBasedComponent>
                </div>
              </td>
            </tr>
          ))}
          
          {/* Total Row */}
          <tr style={{ 
            borderTop: '2px solid #E5E7EB', 
            backgroundColor: '#F9FAFB',
            fontWeight: 'bold' 
          }}>
            <td colSpan="3" style={{ 
              textAlign: 'right', 
              paddingRight: '20px',
              fontSize: '15px',
              color: '#374151'
            }}>
              Total Expenses:
            </td>
            <td style={{ 
              textAlign: 'right', 
              fontSize: '18px', 
              color: '#004080',
              fontWeight: '700'
            }}>
              {formatCurrency(totalAmount)}
            </td>
            <td></td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default ExpenseTable;
